import React, { useMemo } from 'react';
import { merge } from 'lodash';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { AddCircle } from '@mui/icons-material';
import { matchesSearch } from 'utils/formatters';
import { DialogHeader } from 'components/shared/Dialog';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { CheckboxField, InputField, RadioGroupField } from 'components/form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';

import { Charge } from '../../types';
import { formatCurrency } from '../../../../utils';
import { addValueFieldToCharges } from '../../lib/charges';
import { isNotNullOrUndefined } from '../../../../utils/typeGuargs';
import { defaultCharges, ChargeDefinition, ChargeType, newChargeRow, sortByOptions } from '../../constants';

type AddChargeFormValue = {
  filters: { search: string; type: ChargeType };
  charges: Record<ChargeType, (ChargeDefinition & { value: boolean })[]>;
};

export const EquipmentChargesDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.ChargesEquipmentDialog);

  const { control, getValues, handleSubmit } = useForm<AddChargeFormValue>({
    defaultValues: {
      filters: { search: '', type: ChargeType.TaxesAndFees },
      charges: addValueFieldToCharges(
        merge({ ...defaultCharges, ...(data?.externalCharges ?? {}) }),
        new Set(data?.preselectedOptionLabels ?? [])
      ),
    },
  });

  const chargeTypeFilterValue = useWatch({ control, name: 'filters.type' });
  const chargesByType = useWatch({ control, name: 'charges' });
  const charges = chargesByType[chargeTypeFilterValue];

  const filters = useWatch({ control, name: 'filters' });

  const filteredFields = useMemo(() => {
    const searchQuery = filters.search;

    return (charges ?? []).filter((_, index) =>
      matchesSearch(getValues(`charges.${chargeTypeFilterValue}.${index}.label`), searchQuery)
    );
  }, [filters.search, charges, getValues, chargeTypeFilterValue]);

  const hasSearchResults = useMemo(() => [filteredFields.length].some(Boolean), [filteredFields]);

  const onSubmit: SubmitHandler<AddChargeFormValue> = ({ charges }) => {
    const chargesToSet = Object.values(charges).reduce<Omit<Charge, 'groupBucket'>[]>((acc, value) => {
      const selectedOptions = value.filter(({ value }) => Boolean(value));

      selectedOptions.forEach(({ type, label, category, price = 0 }) => {
        const chargePrice = { ...newChargeRow, wholesalePrice: price, retailPrice: price };

        acc.push({ label, type: category, financeType: type, price: chargePrice });
      });

      return acc;
    }, []);

    data.onConfirm(chargesToSet);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={isOpen} onClose={handleClose}>
      <DialogHeader label="Add Equipment Charges" handleClose={handleClose} />

      <DialogContent dividers>
        <Stack gap="1rem">
          <Stack gap="1rem">
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
              <InputField
                name="filters.search"
                control={control}
                inputProps={{ variant: 'outlined', placeholder: 'Search by Name or Type' }}
              />

              <Button onClick={data?.onOpenCreateChargeDialog} startIcon={<AddCircle />}>
                Create charge
              </Button>
            </Box>

            <RadioGroupField row name="filters.type" label="Sort by" control={control} options={sortByOptions} />
          </Stack>

          {!hasSearchResults && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No charges
            </Typography>
          )}

          <RenderWithCondition condition={!!filteredFields.length}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '1rem' }}>
              {filteredFields.map((charge, index) => {
                const label = isNotNullOrUndefined(charge?.price)
                  ? `${charge.label} ${formatCurrency(charge.price, true)}`
                  : charge.label;

                return (
                  <CheckboxField
                    key={index}
                    label={label}
                    control={control}
                    name={`charges.${chargeTypeFilterValue}.${index}.value`}
                  />
                );
              })}
            </Box>
          </RenderWithCondition>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>

        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
